<template>
  <base-modal
    name="basic-modal"
    :visible="show"
    close-button
    @close="closeModal"
  >
    <!-- Create Form View -->
    <div v-if="!showFileVerificationModal">
      <h3 class="text-7 font-bold leading-[1.5] text-color-1 mb-7">
        {{ $t('MassPayments | create') }}
      </h3>

      <div class="mb-10">
        <base-input
          v-model="formData.name"
          :placeholder="$t('MassPayments | namePlaceholder')"
        />

        <div class="text-xs text-light mt-1">
          {{ $t('MassPayments | nameDescription') }}
        </div>
      </div>

      <div class="flex flex-col gap-y-10">
        <div class="flex flex-col gap-y-3">
          <div class="text-sm text-light font-medium">
            {{ $t('MassPayments | warningCsv') }}
            <div
              class="inline text-sm text-brand cursor-pointer"
              @click="onDownload"
            >
              {{ $t('MassPayments | download') }}
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-y-4">
        <base-button
          class="mt-10 !w-full"
          size="md"
          @onClick="submitForm(TABS.list)"
        >
          <spinner v-if="loading" :stroke-width="3" />

          <template v-else>
            {{ $t('MassPayments | uploadList') }}
          </template>
        </base-button>

        <base-button
          class="!w-full"
          type="shade"
          size="md"
          :disabled="isSubmitting"
          @click="submitForm(TABS.manual)"
        >
          <spinner v-if="loading || isSubmitting" :stroke-width="3" />

          <template v-else>
            {{ $t('DetailMassPayments | empty | add') }}
          </template>
        </base-button>
      </div>

      <input
        ref="fileInputRef"
        type="file"
        accept=".csv,.xlsx,.xls"
        class="hidden"
        @change="handleFileSelect"
        @click.stop
      />
    </div>

    <!-- File Verification View -->
    <div v-else class="flex flex-col">
      <div class="flex flex-col mb-9">
        <div class="mb-9">
          <h3 class="text-7 font-bold text-color-1">
            {{ $t('ModalFileVerification | title') }}
          </h3>
        </div>

        <div class="flex flex-col gap-y-4">
          <div class="flex items-center justify-between">
            <div class="font-medium text-light">
              {{ $t('ModalFileVerification | fileUploaded') }}
            </div>

            <div class="flex-grow exchange-dots-separator mx-2" />

            <div class="font-medium text-color-1">
              {{ uploadedFileName }}
            </div>
          </div>

          <div class="flex items-center justify-between">
            <div class="font-medium text-light">
              {{ $t('ModalFileVerification | paymentsAdded') }}
            </div>

            <div class="flex-grow exchange-dots-separator mx-2" />

            <div class="font-medium text-color-1">
              {{ paymentsCount }}
            </div>
          </div>

          <div
            v-for="rowError in uploadErrors?.row_errors || []"
            :key="rowError.row_number"
            class="border rounded-3 bg-bg-1 border-color-2/7 py-4 px-6 mt-2"
          >
            <div class="flex items-center py-2">
              <div class="text-color-1 font-medium">
                {{
                  $t('ModalFileVerification | row', {
                    number: rowError.row_number,
                  })
                }}
              </div>

              <div class="flex-grow exchange-dots-separator mx-2"></div>

              <div class="text-error font-medium">
                {{
                  rowError.errors
                    .map((error) =>
                      $t(`ModalFileVerification | errors | ${error}`)
                    )
                    .join(', ')
                }}
              </div>
            </div>
          </div>

          <div class="text-base font-medium text-light leading-default">
            {{ $t('ModalFileVerification | description') }}
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-y-4">
        <base-button
          class="!w-full"
          type="shade"
          size="md"
          @onClick="closeModal"
        >
          <div>{{ $t('ModalFileVerification | close') }}</div>
        </base-button>
      </div>
    </div>
  </base-modal>
</template>

<script setup>
import { computed, nextTick, reactive, ref } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

import BaseModal from '@emcd-shared/base-modal/BaseModal.vue'
import BaseInput from '@emcd-shared/base-input'
import BaseButton from '@emcd-shared/base-button'
import { Spinner } from '@emcd-shared/base-loader'

import {
  MASS_PAYMENTS_DISPATCHERS,
  MASS_PAYMENTS_GETTERS,
} from '@/shared/constants/store-constants/massPayments'
import { MASS_PAYMENTS_ROUTES } from '@/shared/constants'
import { DETAIL_MASS_PAYMENT_DISPATCHERS } from '@/shared/constants/store-constants/detailMassPayment'
import { TOAST_DISPATCHERS } from '@/shared/constants/store-constants/toast'
import segmentService from '@/shared/services/segment.service'
import { ANALYTICS_EVENT_TYPE } from '@/packages/auth-service/requiredUserAction/shared/utils/analytics'

const MAX_FILE_SIZE_MB = 5
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024

const TABS = {
  list: 'list',
  manual: 'manual',
}

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
})

const emits = defineEmits(['update:modelValue', 'successManual'])

const store = useStore()
const router = useRouter()
const { t, locale } = useI18n()

const fileInputRef = ref(null)
const isSubmitting = ref(false)

const massPaymentId = ref(null)
const showFileVerificationModal = ref(false)
const uploadedFileName = ref('')
const uploadErrors = ref(null)

const show = computed({
  get() {
    return props.modelValue
  },
  set(value) {
    emits('update:modelValue', value)
  },
})

const loading = computed(() => store.getters[MASS_PAYMENTS_GETTERS.loading])

const paymentsCount = computed(() => {
  if (!uploadErrors.value) return ''
  return t('ModalFileVerification | paymentsCount', {
    current: uploadErrors.value.total - uploadErrors.value.total_row_errors,
    total: uploadErrors.value.total,
  })
})

const formData = reactive({
  name: '',
  agreementChecked: true,
})

function closeModal() {
  show.value = false

  resetFormData()
}

function parseErrors(payload) {
  uploadErrors.value = payload
}

function onDownload() {
  store.dispatch(DETAIL_MASS_PAYMENT_DISPATCHERS.goToDownloadCSVExample)
}

function openFileDialog() {
  // Используем nextTick чтобы избежать закрытия модалки при открытии диалога выбора файла
  nextTick(() => {
    // Дополнительная небольшая задержка для гарантии
    setTimeout(() => {
      fileInputRef.value?.click()
    }, 0)
  })
}

async function handleFileSelect(event) {
  const file = event.target.files?.[0]
  if (file) {
    // Проверяем размер файла
    if (file.size > MAX_FILE_SIZE_BYTES) {
      store.dispatch(TOAST_DISPATCHERS.setErrorMessage, {
        message: t('MassPayments | error | fileTooLarge'),
      })
      event.target.value = ''
      return
    }

    // Получаем название файла
    const fileName = file.name

    // Если поле name пустое, используем название файла без расширения
    const nameToUse = formData.name.trim() || fileName.replace(/\.[^/.]+$/, '')

    // Создаем массовую выплату
    const { success, payload } = await store.dispatch(
      MASS_PAYMENTS_DISPATCHERS.createMassPayment,
      { ...formData, name: nameToUse }
    )

    if (success) {
      massPaymentId.value = payload.mass_payment_info.id

      await store.dispatch(
        DETAIL_MASS_PAYMENT_DISPATCHERS.setId,
        massPaymentId.value
      )

      // Загружаем файл в созданную выплату
      const { payload: uploadPayload } = await store.dispatch(
        DETAIL_MASS_PAYMENT_DISPATCHERS.goToUploadInviteFile,
        file
      )

      if (uploadPayload && uploadPayload.total_row_errors > 0) {
        // Есть ошибки, показываем модал
        parseErrors(uploadPayload)
        uploadedFileName.value = fileName
        showFileVerificationModal.value = true
      } else {
        // Нет ошибок, перенаправляем
        closeModal()

        router.push({
          name: MASS_PAYMENTS_ROUTES.DETAIL,
          params: { id: massPaymentId.value },
        })
      }
    }
  }

  // Сбрасываем значение input, чтобы можно было выбрать тот же файл снова
  event.target.value = ''
}

function generateAutoName() {
  const now = new Date()
  const currentLocale = locale.value === 'ru' ? 'ru-RU' : 'en-US'

  // Форматируем дату: "19 января"
  const dateFormatter = new Intl.DateTimeFormat(currentLocale, {
    day: 'numeric',
    month: 'long',
  })

  // Форматируем время: "15:42"
  const timeFormatter = new Intl.DateTimeFormat(currentLocale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })

  const formattedDate = dateFormatter.format(now)
  const formattedTime = timeFormatter.format(now)

  return t('MassPayments | autoGeneratedName', {
    date: `${formattedDate}, ${formattedTime}`,
  })
}

async function submitForm(type) {
  if (loading.value || isSubmitting.value) return

  if (!formData.agreementChecked) return

  if (type === TABS.list) {
    segmentService.trackNewEvent(ANALYTICS_EVENT_TYPE.CLICK, {
      event: ANALYTICS_EVENT_TYPE.CLICK,
      page: 'mass_payments',
      screen: 'form_mass_payout',
      button_name: 'upload_csv',
    })

    // Для загрузки файла просто открываем диалог
    // Создание выплаты произойдет в handleFileSelect после выбора файла
    openFileDialog()
    return
  }

  // Для ручного добавления создаем выплату с автогенерированным именем
  segmentService.trackNewEvent(ANALYTICS_EVENT_TYPE.CLICK, {
    event: ANALYTICS_EVENT_TYPE.CLICK,
    page: 'mass_payments',
    screen: 'form_mass_payout',
    button_name: 'add_manually',
  })

  isSubmitting.value = true
  const nameToUse = formData.name.trim() || generateAutoName()

  try {
    const { success, payload } = await store.dispatch(
      MASS_PAYMENTS_DISPATCHERS.createMassPayment,
      { ...formData, name: nameToUse }
    )

    if (success) {
      massPaymentId.value = payload.mass_payment_info.id

      await store.dispatch(
        DETAIL_MASS_PAYMENT_DISPATCHERS.setId,
        massPaymentId.value
      )

      emits('successManual')
      closeModal()
    }
  } finally {
    isSubmitting.value = false
  }
}

function backToCreateForm() {
  showFileVerificationModal.value = false
  uploadedFileName.value = ''
  uploadErrors.value = null
}

function resetFormData() {
  formData.name = ''
  showFileVerificationModal.value = false
  uploadedFileName.value = ''
  uploadErrors.value = null
}
</script>

<style lang="scss" scoped>
.exchange-dots-separator {
  height: 1px;
  background-image: repeating-linear-gradient(
    90deg,
    rgba(255, 254, 254, 0.1) 0,
    rgba(255, 254, 254, 0.1) 2px,
    transparent 0,
    transparent 8px
  );
}
</style>
